'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeSwitcher } from '../ui/adapters/ThemeSwitcher';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@repo/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';

// Custom hook for media queries
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // Initial check (defaulting to true for SSR)
    if (typeof window !== 'undefined') {
      setMatches(window.matchMedia(query).matches);
    }

    // Set up listener for changes
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern approach
    mediaQuery.addEventListener('change', handleChange);
    
    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

interface NavigationItem {
  href: string;
  label: string;
  children?: NavigationItem[];
}

interface NavigationProps {
  headerOpacity?: number;
}

export function Navigation({ headerOpacity = 1 }: NavigationProps): React.ReactElement {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const router = useRouter();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenSubMenu, setMobileOpenSubMenu] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  
  // Responsive design for navigation
  const [displayWidth, setDisplayWidth] = useState(0);
  
  useEffect(() => {
    // Only run on client
    if (typeof window !== 'undefined') {
      const checkWidth = () => {
        setDisplayWidth(window.innerWidth);
      };
      
      // Initial check
      checkWidth();
      
      // Listen for resize
      window.addEventListener('resize', checkWidth);
      return () => window.removeEventListener('resize', checkWidth);
    }
  }, []);
  
  // Show full nav only on larger screens
  const showFullNav = displayWidth >= 1024; // Standard desktop breakpoint
  const showBurgerNav = !showFullNav;
  
  // Track scroll position to change nav style
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const mobileMenuContainer = document.querySelector('.mobile-menu-container');
      const mobileMenuButton = document.querySelector('.mobile-menu-button');
      
      if (
        mobileMenuOpen &&
        mobileMenuContainer && 
        !mobileMenuContainer.contains(event.target as Node) &&
        mobileMenuButton &&
        !mobileMenuButton.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    
    // Use capture phase to ensure we catch the event before it's stopped
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenSubMenu(null);
  }, [pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleDropdown = (href: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    if (openDropdown === href) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(href);
    }
  };

  const toggleMobileSubMenu = (href: string) => {
    if (mobileOpenSubMenu === href) {
      setMobileOpenSubMenu(null);
    } else {
      setMobileOpenSubMenu(href);
    }
  };

  // Navigation items - public routes that don't require authentication
  const publicItems: NavigationItem[] = [
    { 
      href: '/products',
      label: 'Products',
      children: [
        { href: '/products/fridge-bottles', label: 'Fridge Bottles' },
        { href: '/products/kids-bottles', label: 'Kids Water Bottles' },
        { href: '/products/dry-fruit-containers', label: 'Dry Fruit Pet Can Jars' },
        { href: '/products/makhana-containers', label: 'Makhana Pet Can Jars' },
        { href: '/products/household-jars', label: 'Household Jars' },
        { href: '/products/custom-packaging', label: 'Custom Packaging' }
      ]
    },
    { href: '/industries', label: 'Industries We Serve' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Contact Us' }
  ];
  
  // Mobile items should also include Home
  const mobileItems: NavigationItem[] = publicItems;

  // Dashboard items - protected routes that require authentication
  const dashboardItems: NavigationItem[] = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/dashboard/profile', label: 'Profile' },
    { href: '/dashboard/settings', label: 'Settings' }
  ];

  // Combined navigation items based on auth state
  const navigationItems: NavigationItem[] = user ? [...publicItems, ...dashboardItems] : publicItems;

  // Main navigation items for header
  const mainNavItems = publicItems;

  // Helper function to check if a route is active
  const isRouteActive = (item: NavigationItem): boolean => {
    if (!pathname) return false;
    
    // Check if current path matches item's href directly
    if (pathname === item.href) return true;
    
    // Check if any child route matches
    if (item.children) {
      return item.children.some(child => 
        pathname === child.href || pathname.startsWith(child.href)
      );
    }
    
    return false;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)]">
      <div 
        className={`p-2 sm:p-3 transition-[padding,opacity] duration-300 overflow-visible`}
        style={{ opacity: headerOpacity }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 overflow-visible">
          <div className={`premium-radius-xl px-3 sm:px-4 py-1 flex items-center justify-between transition-all duration-300 overflow-visible ${
            scrolled 
              ? 'premium-glass-nav dark:premium-glass-dark-nav premium-depth-lg dark:premium-depth-xl bg-white/70 backdrop-blur-sm py-1 sm:py-1.5' 
              : 'bg-transparent py-1.5 sm:py-2'
          }`}>
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link 
                href="/" 
                className="flex items-center"
                aria-label="PAVISHA Home"
              >
                <span className="premium-gradient-text text-sm sm:text-base md:text-lg font-bold">PAVISHA</span>
              </Link>
            </div>

            {/* Desktop Navigation - Center aligned with enhanced premium glass effect */}
            <nav 
              className={`${showFullNav ? "flex" : "hidden"} items-center gap-2 sm:gap-6 relative z-20 overflow-visible`} 
              ref={navRef}
              role="navigation"
              aria-label="Main Navigation"
            >
              {mainNavItems.map((item) => (
                <div key={item.href} className="relative overflow-visible">
                  {item.children ? (
                    <div className="relative overflow-visible">
                      <button
                        onClick={(e) => toggleDropdown(item.href, e)}
                        className={`text-sm font-medium px-2.5 py-1 rounded-md transition-colors duration-200 flex items-center ${
                          isRouteActive(item)
                            ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
                            : `${scrolled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-700 dark:text-gray-200'} hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`
                        }`}
                        aria-expanded={openDropdown === item.href}
                        aria-controls={`dropdown-${item.href.replace('/', '-')}`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown 
                          className={`ml-1 h-4 w-4 transition-transform duration-200 ${openDropdown === item.href ? 'transform rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      
                      <AnimatePresence>
                        {openDropdown === item.href && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 z-[9999] min-w-[220px]"
                            style={{ 
                              top: "calc(100% + 10px)",
                              filter: "drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))",
                              minWidth: "220px",
                              position: "absolute"
                            }}
                            id={`dropdown-${item.href.replace('/', '-')}`}
                            role="menu"
                            aria-orientation="vertical"
                          >
                            <div
                              className={`premium-radius-lg overflow-hidden ${
                                scrolled 
                                  ? 'premium-glass-nav dark:premium-glass-nav bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm' 
                                  : 'bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm'
                              }`}
                            >
                              <div className="py-1">
                                {item.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                                      pathname === child.href
                                        ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
                                        : "text-gray-700 dark:text-gray-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400"
                                    }`}
                                    onClick={() => setOpenDropdown(null)}
                                    role="menuitem"
                                  >
                                    <span>{child.label}</span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className={`text-sm font-medium px-2.5 py-1 rounded-md transition-colors duration-200 ${
                        pathname === item.href
                          ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
                          : `${scrolled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-700 dark:text-gray-200'} hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`
                      }`}
                    >
                      <span>{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {!user ? (
                <>
                  {/* Only show login when the full navigation is visible */}
                  {showFullNav && (
                    <Link
                      href="/auth/login"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 premium-transition-all"
                    >
                      Login
                    </Link>
                  )}
                  
                  {/* Only show on appropriate sized screens and full nav */}
                  {showFullNav && (
                    <Link
                      href="/contact"
                      className="text-xs sm:text-sm font-medium premium-gradient-primary text-white px-2.5 sm:px-4 py-1 sm:py-1.5 premium-radius-md premium-shadow premium-transition-all premium-hover-lift"
                    >
                      Get a Quote
                    </Link>
                  )}
                </>
              ) : (
                <button
                  onClick={handleSignOut}
                  className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                >
                  Sign out
                </button>
              )}
            </div>

            {/* Mobile Menu Button with premium effect */}
            <div className={`${showBurgerNav ? 'flex' : 'hidden'} ml-2 sm:ml-4 items-center h-full`}>
              <button
                type="button"
                aria-label="Toggle menu"
                className="mobile-menu-button inline-flex items-center justify-center p-1.5 sm:p-2 premium-radius-md text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all premium-hover-lift focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                style={{ 
                  willChange: "transform",
                  transform: "translateZ(0)"
                }}
              >
                <span className="sr-only">Toggle menu</span>
                {!mobileMenuOpen ? (
                  <svg className="block h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown with premium glass effect matching navbar */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <div className="mobile-menu-container relative z-50">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  mass: 1
                }}
                className="absolute top-2 right-0 left-0 mx-4 sm:mx-auto sm:max-w-md p-4 premium-radius-xl premium-glass-nav dark:premium-glass-dark-nav premium-depth-lg dark:premium-depth-xl backdrop-blur-sm overflow-hidden"
                style={{ 
                  transform: "translate3d(0, 0, 0)",
                  willChange: "transform, opacity",
                  backfaceVisibility: "hidden"
                }}
              >
                <div className="pb-2 relative z-10">
                  {/* Navigation Items with premium glass effect */}
                  {mobileItems.map((item) => (
                    <div key={item.href}>
                      {item.children ? (
                        <div className="mb-1">
                          <button
                            onClick={() => toggleMobileSubMenu(item.href)}
                            className={`flex justify-between items-center w-full py-2.5 text-sm font-medium premium-radius-md ${
                              isRouteActive(item) ? 'mobile-nav-link-active' : 'mobile-nav-link'
                            }`}
                            aria-expanded={mobileOpenSubMenu === item.href}
                            aria-controls={`mobile-submenu-${item.href.replace('/', '-')}`}
                          >
                            <span>{item.label}</span>
                            <ChevronDown 
                              className={`h-4 w-4 transition-transform duration-200 ${mobileOpenSubMenu === item.href ? 'transform rotate-180' : ''}`}
                              aria-hidden="true"
                            />
                          </button>
                          
                          <AnimatePresence>
                            {mobileOpenSubMenu === item.href && (
                              <motion.div
                                initial={{ height: 0, opacity: 0.5 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0.5 }}
                                transition={{ 
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 30,
                                  mass: 1
                                }}
                                className="pl-2 ml-3 border-l-2 border-gray-200 dark:border-gray-700 overflow-hidden"
                                id={`mobile-submenu-${item.href.replace('/', '-')}`}
                                role="menu"
                              >
                                {item.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    className={`flex items-center py-2 pl-3 text-sm ${
                                      pathname === child.href
                                        ? 'text-red-600 dark:text-red-400 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    role="menuitem"
                                  >
                                    <ChevronRight className="h-3 w-3 mr-1 flex-shrink-0" aria-hidden="true" />
                                    <span>{child.label}</span>
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          href={item.href}
                          className={`block py-2.5 text-sm font-medium premium-radius-md ${
                            pathname === item.href ? 'mobile-nav-link-active' : 'mobile-nav-link'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span>{item.label}</span>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Auth Actions with premium styling */}
                <div className="pt-3 pb-1 border-t border-gray-100 dark:border-gray-800 relative z-10">
                  {!user ? (
                    <div className="flex flex-col space-y-2">
                      <Link
                        href="/auth/login"
                        className="block py-2.5 text-sm font-medium premium-radius-md mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Log in to your account"
                      >
                        Login
                      </Link>
                      <Link
                        href="/contact"
                        className="block py-2.5 text-sm font-medium premium-gradient-primary text-white premium-radius-md premium-shadow text-center"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Get a quote for our products"
                      >
                        Get a Quote
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        handleSignOut();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 premium-radius-md"
                    >
                      Sign out
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
} 